"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Quote, Star, ArrowRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  author: "Maria S.",
  role: "Cliente Satisfeita",
  quote: "Recuperei a saúde dos meus fios com o tratamento personalizado da Dil Saúde Capilar. A diferença na textura e volume é visível, e minha autoestima foi renovada.",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/maria_before_scalp.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/maria_after_scalp.png"
}, {
  id: 2,
  author: "Ana P.",
  role: "Paciente",
  quote: "Profissionais dedicados e tecnologia de ponta fizeram toda a diferença na minha recuperação capilar. O diagnóstico preciso foi o ponto de virada para mim.",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/ana_before_hair.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/ana_after_hair.png"
}, {
  id: 3,
  author: "Juliana M.",
  role: "Paciente de Tricologia",
  quote: "Eu não acreditava que poderia reverter a queda, mas o protocolo baseado em evidências funcionou perfeitamente. O ambiente acolhedor me fez sentir segura.",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/juliana_before_treatment.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/juliana_after_treatment.png"
}];
const TestimonialSlide = ({
  data,
  index
}: {
  data: (typeof testimonials)[0];
  index: number;
}) => {
  const [showAfter, setShowAfter] = useState(true);
  return <div className="flex flex-col items-center text-center px-4 md:px-12 py-8" data-index={index}>
      {}
      <div className="relative mb-8 group">
        <div className="absolute -inset-1 rounded-full bg-[var(--chart-3)] opacity-30 blur-md transition-opacity duration-500 group-hover:opacity-50"></div>
        <SafeHydrate><Avatar className="size-40 md:size-56 border-4 border-[var(--chart-3)] shadow-2xl">
          <AvatarImage src={showAfter ? data.afterImg : data.beforeImg} alt={`Resultado de ${data.author} - ${showAfter ? "Depois" : "Antes"}`} className="object-cover transition-all duration-700 ease-in-out" />
          <AvatarFallback className="text-4xl bg-muted text-muted-foreground">
            {data.author.charAt(0)}
          </AvatarFallback>
        </Avatar></SafeHydrate>
        
        {}
        <div className="absolute bottom-0 right-0 translate-x-1/4 -translate-y-1/4 bg-[var(--chart-3)] text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg uppercase tracking-wider">
          {showAfter ? "Depois" : "Antes"}
        </div>
      </div>

      {}
      <div className="flex items-center gap-4 mb-8 bg-white/5 px-6 py-2 rounded-full backdrop-blur-sm border border-white/10">
        <span className={`text-sm font-medium transition-colors ${!showAfter ? "text-[var(--chart-3)]" : "text-secondary-foreground/60"}`}>
          Antes
        </span>
        <Switch checked={showAfter} onCheckedChange={setShowAfter} className="data-[state=checked]:bg-[var(--chart-3)] data-[state=unchecked]:bg-input/50" aria-label="Alternar entre antes e depois" />
        <span className={`text-sm font-medium transition-colors ${showAfter ? "text-[var(--chart-3)]" : "text-secondary-foreground/60"}`}>
          Depois
        </span>
      </div>

      {}
      <div className="max-w-3xl relative">
        <Quote className="absolute -top-8 -left-8 size-12 text-[var(--chart-3)]/20 rotate-180" />
        <blockquote className="font-serif text-2xl md:text-3xl lg:text-4xl leading-relaxed text-secondary-foreground italic">
          "{data.quote}"
        </blockquote>
        <Quote className="absolute -bottom-8 -right-8 size-12 text-[var(--chart-3)]/20" />
      </div>

      {}
      <div className="mt-8 space-y-1">
        <h3 className="text-xl font-semibold text-[var(--chart-3)]">
          {data.author}
        </h3>
        <p className="text-sm text-secondary-foreground/70 uppercase tracking-widest">
          {data.role}
        </p>
        <div className="flex gap-1 justify-center mt-2 text-[var(--chart-3)]">
          {[...Array(5)].map((_, i) => <Star key={i} className="size-4 fill-current" />)}
        </div>
      </div>
    </div>;
};
export default function VozesDeTransformacao() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="vozes-de-transformação" className="relative w-full py-24 overflow-hidden bg-secondary text-secondary-foreground">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-secondary to-transparent opacity-50" />
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-secondary to-transparent opacity-50" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[var(--chart-3)]/5 rounded-full blur-3xl" />
      </div>

      <div ref={ref} className={`container mx-auto px-4 relative z-10 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        {}
        <div className="text-center mb-16 space-y-4">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-secondary-foreground">
            Vozes de <span className="text-[var(--chart-3)]">Transformação</span>
          </h2>
          <p className="text-secondary-foreground/80 max-w-2xl mx-auto text-lg font-light">
            Histórias reais de mulheres que recuperaram sua autoestima e saúde
            capilar através de nossos protocolos exclusivos.
          </p>
        </div>

        {}
        <div className="max-w-5xl mx-auto">
          <Carousel opts={{
          align: "center",
          loop: true
        }} className="w-full">
            <CarouselContent>
              {testimonials.map((testimonial, index) => <CarouselItem key={testimonial.id}>
                  <TestimonialSlide data={testimonial} index={index} />
                </CarouselItem>)}
            </CarouselContent>

            {}
            <div className="hidden md:block">
              <CarouselPrevious className="left-0 lg:-left-12 bg-transparent border-secondary-foreground/20 text-secondary-foreground hover:bg-[var(--chart-3)] hover:text-white hover:border-[var(--chart-3)] transition-all" />
              <CarouselNext className="right-0 lg:-right-12 bg-transparent border-secondary-foreground/20 text-secondary-foreground hover:bg-[var(--chart-3)] hover:text-white hover:border-[var(--chart-3)] transition-all" />
            </div>
          </Carousel>
        </div>

        {}
        <div className="mt-16 text-center">
          <Button asChild size="lg" className="bg-[var(--chart-3)] hover:bg-[var(--chart-3)]/90 text-white rounded-full px-8 py-6 text-lg shadow-[0_0_20px_rgba(234,179,8,0.2)]">
            <Link to="/SchedulingPage">
              Agende sua Avaliação <ArrowRight className="ml-2 size-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}